import React from 'react';

import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import useTeamMembers from '../hooks/useTeamMembers';
import { Team } from '../types/thematicGroups';
import PaginationComponent from 'components/Pagination/Pagination';

interface TeamMembersModalProps {
  open: boolean;
  onClose: () => void;
  team: Team | null;
  loggedUserId?: string;
}

const TeamMembersModal: React.FC<TeamMembersModalProps> = ({
  open,
  onClose,
  team,
  loggedUserId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { members, loading, totalPages, currentPage, setPage } = useTeamMembers({
    teamUuid: team?.uuid || '',
    page: 1,
    size: 20,
  });

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const isUserConnection = (memberUuid: string) => {
    if (!team || !loggedUserId) return false;

    return team.in_common_connections.some(connection => connection.id === memberUuid);
  };

  // Ordena os membros: conexões primeiro, depois ordem alfabética em cada grupo
  const sortedMembers = [...members].sort((a, b) => {
    const aIsConnection = isUserConnection(a.uuid);
    const bIsConnection = isUserConnection(b.uuid);

    // Se um é conexão e o outro não, a conexão vem primeiro
    if (aIsConnection && !bIsConnection) return -1;
    if (!aIsConnection && bIsConnection) return 1;

    // Se ambos são do mesmo tipo, ordena alfabeticamente
    const aName = `${a.first_name} ${a.last_name}`.toLowerCase();
    const bName = `${b.first_name} ${b.last_name}`.toLowerCase();
    return aName.localeCompare(bName);
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: isMobile ? 0 : 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          MEMBROS
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <Typography>Carregando membros...</Typography>
          </Box>
        ) : (
          <>
            <Stack spacing={1} mt={2}>
              {sortedMembers.map((member, index) => {
                const isConnection = isUserConnection(member.uuid);

                return (
                  <Box
                    key={member.uuid}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.default,
                      borderLeft: isConnection
                        ? `2px solid ${theme.palette.primary.main}`
                        : '1px solid transparent',
                      '&:hover': {
                        backgroundColor: theme.palette.background.default,
                        transform: 'translateY(-1px)',
                        transition: 'all 0.2s ease',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Avatar
                      src={member.photo_url}
                      alt={`${member.first_name} ${member.last_name}`}
                      sx={{
                        width: 48,
                        height: 48,
                        //border: isConnection ? `2px solid ${theme.palette.primary.main}` : 'none',
                      }}
                    >
                      {member.first_name.charAt(0)}
                      {member.last_name.charAt(0)}
                    </Avatar>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body1"
                        fontWeight={isConnection ? 'bold' : 'normal'}
                        color="text.primary"
                      >
                        {member.first_name} {member.last_name}
                      </Typography>
                      <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                        {member.team_position}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>

            {totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                p={2}
                mt={2}
                sx={{
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <PaginationComponent
                  totalPages={totalPages}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  size="small"
                  color="custom"
                  customColor={theme.palette.warning.main}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            px: 3,
            py: 1.5,
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: 2,
            backgroundColor: 'transparent',
            color: theme.palette.primary.main,
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 500,
            transition: 'all 0.2s',
            width: '50%',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.background.paper,
            },
          }}
        >
          Fechar
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TeamMembersModal;
